import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const Home = () => {
  useEffect(() => {
    // SEO: Update page title and meta description for homepage
    document.title = 'KPCA - Join Kenya Professional Cleaners Association | Get Certified Today';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Join KPCA, Kenya\'s premier professional cleaning association. Get certified, connect with 500+ industry professionals. Transform your cleaning business today.');
    }
  }, []);

  return (
    <>
      <SEOHead 
        title="KPCA - Join Kenya Professional Cleaners Association | Get Certified Today"
        description="Join KPCA, Kenya's premier professional cleaning association. Get certified, connect with 500+ industry professionals. Transform your cleaning business today with recognized certification and comprehensive support."
        keywords="Kenya Professional Cleaners Association, KPCA membership, professional cleaning certification, cleaning business Kenya, join KPCA, cleaning industry certification, professional cleaners association"
        url="https://kpca.co.ke/"
        type="website"
      />
      <main className="min-h-screen overflow-hidden">
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-hero text-white min-h-screen flex items-center overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-pulse-slow"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-orange-300 rounded-full mix-blend-multiply filter blur-xl animate-pulse-slow animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-80 h-80 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-pulse-slow animation-delay-4000"></div>
        </div>
        
        {/* Floating particles effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-ping opacity-75"></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-yellow-300 rounded-full animate-ping opacity-50 animation-delay-1000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-orange-300 rounded-full animate-ping opacity-60 animation-delay-2000"></div>
          <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-white rounded-full animate-ping opacity-70 animation-delay-3000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left space-y-8 animate-fade-in-up">
              <div className="space-y-4">
                <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium text-white border border-white/30 glass-effect animate-fade-in-down">
                  <svg className="w-4 h-4 mr-2 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                  Kenya's #1 Professional Cleaning Association
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-tight">
                  <span className="block text-shadow-lg">Professional</span>
                  <span className="block text-yellow-300 text-shadow-lg">Cleaning</span>
                  <span className="block text-shadow-lg">Excellence</span>
                </h1>
                
                <p className="text-xl sm:text-2xl text-blue-100 font-light leading-relaxed max-w-2xl mx-auto lg:mx-0">
                  The official body for <span className="font-semibold text-yellow-300">professional cleaning services</span> in Kenya. 
                  Connecting certified cleaners with quality-conscious clients since 2009.
                </p>
              </div>

              {/* Enhanced Stats */}
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0">
                <div className="text-center glass-effect rounded-2xl p-4 hover-glow">
                  <div className="text-2xl sm:text-3xl font-bold text-yellow-300">500+</div>
                  <div className="text-xs sm:text-sm text-blue-200">Members</div>
                </div>
                <div className="text-center glass-effect rounded-2xl p-4 hover-glow">
                  <div className="text-2xl sm:text-3xl font-bold text-yellow-300">15+</div>
                  <div className="text-xs sm:text-sm text-blue-200">Years</div>
                </div>
                <div className="text-center glass-effect rounded-2xl p-4 hover-glow">
                  <div className="text-2xl sm:text-3xl font-bold text-yellow-300">2000+</div>
                  <div className="text-xs sm:text-sm text-blue-200">Trained</div>
                </div>
              </div>

              {/* Enhanced CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/register"
                  className="group relative bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-2xl text-lg font-bold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/25 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative flex items-center justify-center">
                    Join KPCA Today
                    <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </Link>
                
                <Link
                  to="/verify-member"
                  className="group bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-2xl text-lg font-semibold transition-all duration-300 transform hover:scale-105 backdrop-blur-md glass-effect"
                >
                  <span className="flex items-center justify-center">
                    Verify Members
                    <svg className="w-5 h-5 ml-2 transform group-hover:rotate-45 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                </Link>
              </div>
            </div>

            {/* Right Visual Element */}
            <div className="relative animate-fade-in-right">
              <div className="relative w-full max-w-lg mx-auto">
                {/* Main circle with gradient */}
                <div className="w-80 h-80 lg:w-96 lg:h-96 mx-auto bg-gradient-to-br from-white/20 to-white/5 rounded-full backdrop-blur-md border border-white/30 flex items-center justify-center glass-effect hover-glow">
                  <div className="w-64 h-64 lg:w-80 lg:h-80 bg-gradient-to-br from-orange-400/30 to-blue-500/30 rounded-full flex items-center justify-center">
                    <div className="text-center text-white p-8">
                      <svg className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-4 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="text-xl lg:text-2xl font-bold mb-2">Certified</h3>
                      <p className="text-sm lg:text-base text-blue-100">Professional Excellence</p>
                    </div>
                  </div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute -top-4 -left-4 w-20 h-20 bg-yellow-400/20 rounded-full backdrop-blur-md animate-pulse-slow glass-effect"></div>
                <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-orange-400/20 rounded-full backdrop-blur-md animate-pulse-slow glass-effect animation-delay-2000"></div>
                <div className="absolute top-1/2 -left-8 w-12 h-12 bg-blue-400/20 rounded-full backdrop-blur-md animate-pulse-slow glass-effect animation-delay-1000"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Enhanced About Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary-100 to-orange-100"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6">
              Why Choose <span className="gradient-text">KPCA</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
              We're not just an association – we're your partner in building a successful, professional cleaning business that stands out in Kenya's competitive market.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature cards with enhanced design */}
            <div className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden hover-glow animate-fade-in-up">
              <div className="bg-gradient-to-br from-primary-500 to-primary-700 p-6 text-white">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-2">Industry Recognition</h3>
              </div>
              <div className="p-8">
                <p className="text-gray-600 leading-relaxed mb-4">
                  Get certified with credentials recognized across Kenya and internationally. Build trust with clients through our verified certification program.
                </p>
                <div className="flex items-center text-primary-600 font-semibold group-hover:text-primary-700 transition-colors">
                  <span>Learn more</span>
                  <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden hover-glow animate-fade-in-up animation-delay-200">
              <div className="bg-gradient-to-br from-orange-500 to-orange-700 p-6 text-white">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-2">Quality Standards</h3>
              </div>
              <div className="p-8">
                <p className="text-gray-600 leading-relaxed mb-4">
                  Maintain excellence with established industry standards and best practices.
                </p>
                <p className="text-gray-600 mt-4">
                  Access comprehensive standards framework designed by industry experts. Stay compliant with the latest regulations and requirements.
                </p>
              </div>
            </div>

            <div className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden hover-glow animate-fade-in-up animation-delay-400">
              <div className="bg-gradient-to-br from-green-500 to-green-700 p-6 text-white">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-2">Professional Network</h3>
              </div>
              <div className="p-8">
                <p className="text-gray-600 leading-relaxed mb-4">
                  Connect with 500+ professionals, share opportunities, and grow your business through our exclusive networking platform.
                </p>
                <div className="flex items-center text-primary-600 font-semibold group-hover:text-primary-700 transition-colors">
                  <span>Join network</span>
                  <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-primary-900 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.3),transparent_50%)]"></div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,rgba(249,115,22,0.3),transparent_50%)]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in-up">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6 text-shadow-lg">
              Ready to <span className="gradient-text">Transform</span> Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed font-light">
              Join thousands of successful cleaning professionals who have elevated their careers through KPCA certification and professional development programs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link
                to="/register"
                className="group relative bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-12 py-5 rounded-2xl text-xl font-bold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/25 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                <span className="relative flex items-center">
                  Start Your Journey
                  <svg className="w-6 h-6 ml-3 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Link>
              
              <Link
                to="/contact"
                className="group text-white border-2 border-white/30 hover:border-white/60 px-12 py-5 rounded-2xl text-xl font-semibold transition-all duration-300 transform hover:scale-105 backdrop-blur-md glass-effect hover:bg-white/10"
              >
                <span className="flex items-center">
                  Contact Us
                  <svg className="w-6 h-6 ml-3 transform group-hover:rotate-45 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </span>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 opacity-60">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">500+</div>
                <div className="text-sm text-gray-300">Certified Members</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">15+</div>
                <div className="text-sm text-gray-300">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">2000+</div>
                <div className="text-sm text-gray-300">Professionals Trained</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">98%</div>
                <div className="text-sm text-gray-300">Success Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};

export default Home;